'use-client';
import React, { useState } from 'react';
import classNames from 'clsx';
import Section from '../../Section/viewer/Section';
import type { Pos } from '../../../../providers/useScrollPosition';
import { useScrollPosition } from '../../../../providers/useScrollPosition';
import type {
  HeaderAnimationState,
  IHeaderSectionProps,
} from '../HeaderSection.types';
import styles from '../../Section/viewer/styles/Section.scss';
import semanticClassNames from '../HeaderSection.semanticClassNames';
import headerStyles from './HeaderSection.scss';

const SCROLLED_Y = 2;

const SCROLL_UP_THRESHOLD = 100;

const getDownThreshold = (animationType: IHeaderSectionProps['animate']) => {
  switch (animationType) {
    case 'move':
      return 400;
    case 'fade':
      return 200;
    default:
      return 200;
  }
};

const createAnimationScrollListener = (
  animationType: IHeaderSectionProps['animate'],
  setAnimationClass: Function,
) => {
  let dirChangeStart = 0;
  let scrollState: 'UP' | 'DOWN' = 'DOWN';
  const listener = ({ prevPos, currPos }: { prevPos: Pos; currPos: Pos }) => {
    const currScroll = currPos.y * -1;
    const prevScroll = prevPos.y * -1;
    if (currScroll >= prevScroll) {
      if (scrollState === 'UP') {
        dirChangeStart = prevScroll;
        scrollState = 'DOWN';
      }
      if (currScroll - dirChangeStart > getDownThreshold(animationType)) {
        setAnimationClass('scrolled-down');
      }
    } else {
      if (scrollState === 'DOWN') {
        dirChangeStart = prevScroll;
        scrollState = 'UP';
      }
      if (
        dirChangeStart - currScroll > SCROLL_UP_THRESHOLD ||
        currScroll <= 10
      ) {
        setAnimationClass('scrolled-up');
      }
    }
  };
  return listener;
};

const HeaderSection: React.FC<IHeaderSectionProps> = props => {
  const [scrolled, setScrolled] = React.useState<boolean>(false);

  // bg color change listener
  useScrollPosition(
    ({ currPos }) => {
      if (currPos.y * -1 >= SCROLLED_Y) {
        if (!scrolled) {
          setScrolled(true);
        }
      } else if (scrolled) {
        setScrolled(false);
      }
    },
    [scrolled],
    { disabled: props.skin !== 'RectangleAreaAfterScroll' },
  );

  const [animationState, setAnimationClass] =
    useState<HeaderAnimationState>('');

  // animation listener
  useScrollPosition(
    createAnimationScrollListener(props.animate, setAnimationClass),
    [],
    {
      waitFor: 45,
      disabled: props.animate === 'none',
    },
  );

  const isFadedDown =
    animationState === 'scrolled-down' && props.animate === 'fade';

  return (
    <Section
      {...props}
      semanticClassNames={semanticClassNames}
      className={classNames(props.className, {
        [styles[props.skin]]: true,
        [styles.scrolled]: scrolled,
        [styles.animate]: props.animate !== 'none',
        [styles.move]:
          animationState === 'scrolled-down' && props.animate === 'move',
        [styles.fade]: isFadedDown,
        [styles.headerSectionAddVisibilityTransition]:
          isFadedDown && props.isHeaderSectionAddVisibilityTransitionEnabled,
        [styles.scrollUp]: animationState === 'scrolled-up',
        [headerStyles.withSpacing]: props.isBuilder,
      })}
    />
  );
};

export default HeaderSection;
